import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { message, notification } from "../ui";
import { isTemporaryApiUnavailableError } from "../api/createApiRequest";
import { isApiRequestAborted } from "../api/apiRequestGate";
import { isAppRuntimeRecovering } from "../system/appRuntimeGate";

/**
 * Produktübergreifender Sammel-Zustand für API-Startup-Fehler.
 *
 * Verfolgt Fehler beim Initial-Laden (z. B. mehrere fehlgeschlagene Bootstrap-Requests
 * oder eine erkannte Konnektivitätsstörung) und schaltet dann einen globalen
 * „API nicht erreichbar"-Zustand scharf. Solange dieser aktiv ist, werden
 * Einzel-Fehlertoasts unterdrückt,
 * damit der Nutzer nicht von Dutzenden Folgefehlern überrollt wird.
 *
 * Produktneutral — keine projektspezifischen Importe. mQmarketing und mQguard
 * nutzen denselben Provider; die Darstellung des Fehlerzustands bleibt projektseitig.
 */

const startupFailureThreshold = 2;

export interface GlobalApiFailure {
  source: string;
  message: string;
}

interface GlobalApiErrorState {
  active: boolean;
  failures: GlobalApiFailure[];
  primaryMessage: string;
  reportStartupFailure: (source: string, err: unknown) => void;
  reportStartupFailures: (failures: Array<{ source: string; err: unknown }>) => void;
  clearGlobalError: () => void;
}

const GlobalApiErrorContext = createContext<GlobalApiErrorState | null>(null);

let globalApiErrorActive = false;
let feedbackSuppressionInstalled = false;

const clearVisibleFeedback = () => {
  message.destroy();
  notification.destroy();
};

const getErrorMessage = (err: unknown) => {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === "string" && err.trim()) return err.trim();
  return "ApiServiceUnavailable";
};

export const isLikelyApiConnectivityError = (err: unknown) => {
  if (isTemporaryApiUnavailableError(err)) return true;
  const message = getErrorMessage(err).toLowerCase();
  return (
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("network error") ||
    message.includes("load failed") ||
    message.includes("apiendpointnotfound") ||
    message.includes("apiserviceunavailable") ||
    // Die Startup-Migrationssperre ist vorübergehend. Ein Reload während des
    // Containerstarts darf deshalb keine bestehende Anmeldung verwerfen.
    message.includes("apiserviceupdating") ||
    message.includes("apiunexpectedresponse") ||
    message.includes("unexpected token '<'") ||
    message.includes("unexpected token <") ||
    message.includes("unexpected end of json input")
  );
};

export const isGlobalApiErrorActive = () => globalApiErrorActive || isAppRuntimeRecovering();

/**
 * Patcht die statischen `message.*`-Methoden, sodass sie bei aktivem globalem
 * API-Fehler keine Toasts mehr anzeigen. Einmalig beim App-Start aufrufen.
 * `useFeedback()` unterdrückt App-gebundene Fehler-Toasts über denselben Zustand.
 */
export function installGlobalApiFeedbackSuppression() {
  if (feedbackSuppressionInstalled) return;
  feedbackSuppressionInstalled = true;

  const methods = ["error", "warning", "info", "open"] as const;
  const messageApi = message as unknown as Record<typeof methods[number], (...args: unknown[]) => unknown>;
  methods.forEach((method) => {
    const original = messageApi[method];
    messageApi[method] = (...args: unknown[]) => {
      // Runtime-Folgefehler sperrt das Kit selbst (einschließlich seines schließbaren Promise-Vertrags).
      if (globalApiErrorActive) return Promise.resolve();
      return original(...args);
    };
  });
}

export const GlobalApiErrorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [failures, setFailures] = useState<GlobalApiFailure[]>([]);
  const [active, setActive] = useState(false);

  const reportStartupFailures = useCallback((reportedFailures: Array<{ source: string; err: unknown }>) => {
    const relevantFailures = reportedFailures.filter(({ err }) => !isApiRequestAborted(err)
      && !(isAppRuntimeRecovering() && isLikelyApiConnectivityError(err)));
    const normalized = relevantFailures.map(({ source, err }) => ({ source, message: getErrorMessage(err) }));
    if (normalized.length === 0) return;

    setFailures((current) => {
      const nextFailures = [...current, ...normalized];
      const hasConnectivityError = relevantFailures.some(({ err }) => isLikelyApiConnectivityError(err));
      if (hasConnectivityError || nextFailures.length >= startupFailureThreshold) {
        globalApiErrorActive = true;
        clearVisibleFeedback();
        setActive(true);
      }
      return nextFailures;
    });
  }, []);

  const reportStartupFailure = useCallback((source: string, err: unknown) => {
    reportStartupFailures([{ source, err }]);
  }, [reportStartupFailures]);

  const clearGlobalError = useCallback(() => {
    globalApiErrorActive = false;
    setActive(false);
    setFailures([]);
  }, []);

  const primaryMessage = failures[0]?.message || "ApiServiceUnavailable";
  const value = useMemo<GlobalApiErrorState>(() => ({
    active,
    failures,
    primaryMessage,
    reportStartupFailure,
    reportStartupFailures,
    clearGlobalError,
  }), [active, clearGlobalError, failures, primaryMessage, reportStartupFailure, reportStartupFailures]);

  globalApiErrorActive = active;

  return (
    <GlobalApiErrorContext.Provider value={value}>
      {children}
    </GlobalApiErrorContext.Provider>
  );
};

export const useGlobalApiError = () => {
  const context = useContext(GlobalApiErrorContext);
  if (!context) {
    throw new Error("useGlobalApiError must be used within a GlobalApiErrorProvider");
  }
  return context;
};
